<template>
    <div>
        <b-form-group
            label="Provider:"
            class
            label-cols="4"
            label-for="staff_id"
        >
            <v-select
                id="staffs"
                ref="vselect"
                v-validate="'required'"
                :disabled="isLocked"
                :class="{
                    'border-danger':
                        errors.has('staffs') || formError.includes('staff_id')
                }"
                :options="providers"
                :label="'name'"
                :value="value.staff_id"
                @input="val => onChanged(val, 'staff_id')"
                name="staffs"
                aria-placeholder="true"
                placeholder="Provider (required)"
            ></v-select>
        </b-form-group>

        <b-form-group
            class="med-record"
            label="Given By:"
            label-cols="4"
            label-for="given_by"
        >
            <v-select
                id="given_by"
                :disabled="isLocked"
                ref="vselect"
                :options="givenByOptions"
                :value="value.given_by"
                @input="val => onChanged(val, 'given_by')"
                :label="'name'"
                name="given_by"
                aria-placeholder="true"
                placeholder="Given By (optional)"
            ></v-select>
        </b-form-group>

        <b-form-group
            :label-for="`given_date`"
            :label-cols="4"
            :label="'Given:'"
        >
            <vet-datetime
                id="given_date"
                :disabled="isLocked"
                :value="value.given_date"
                @input="val => onChanged(val, 'given_date')"
                v-validate="'required'"
                :placeholder="{
                    date: 'Date (required)',
                    time: 'Time (required)'
                }"
                :class="{
                    'border-danger': errors.has('given_date')
                }"
                container="#createUpdateRecord"
                rules="required"
                name="given_date"
                format="moment"
            ></vet-datetime>
        </b-form-group>

        <b-form-group
            class="med-record quantity"
            :label-for="`quantity`"
            :label-cols="4"
            label="Quantity:"
        >
            <div class="form-inline">
                <div>
                    <b-form-input
                        id="quantity"
                        :disabled="isLocked"
                        :value="value.quantity"
                        @input="val => onChanged(val, 'quantity')"
                        v-validate="'numeric|required'"
                        :class="{
                            'border-danger': errors.has('quantity')
                        }"
                        name="quantity"
                        placeholder="Quantity (required)"
                        class="col mr-auto"
                    ></b-form-input>
                    <span class="ml-1">
                        {{ value.uom ? value.uom : "Unit(s)" }}
                    </span>
                </div>
            </div>
        </b-form-group>

        <b-form-group
            class="medical-note"
            :label-for="`soap`"
            :label-cols="4"
            label="Medical Note:"
        >
            <b-form-select
                id="soap"
                :disabled="isLocked"
                :value="value.soap"
                @input="val => onChanged(val, 'soap')"
                :options="medicalNotes"
                name="soap"
            ></b-form-select>
        </b-form-group>

        <b-form-group
            class="med-record grooming"
            :label-for="`refill`"
            :label-cols="4"
            label="Refill:"
        >
            <div class="form-inline">
                <b-form-input
                    id="refill"
                    :disabled="isLocked"
                    :value="value.refill"
                    @input="val => onChanged(val, 'refill')"
                    v-validate="'numeric|required'"
                    :class="{ 'border-danger': errors.has('refill') }"
                    name="refill"
                    class="col-2"
                ></b-form-input>
                <span class="ml-2 mr-2">time(s) until</span>
                <date-picker
                    @dp-show="showDatePicker"
                    :config="datePickerOptions"
                    :disabled="isLocked"
                    :value="value.expire"
                    @input="val => onChanged(val, 'expire')"
                    :placeholder="
                        'Date (' +
                        (value.refill > 0 ? 'required' : 'optional') +
                        ')'
                    "
                    name="expire"
                    style="width: 52%;"
                    v-validate="{ required: value.refill > 0 }"
                    :class="{
                        'border-danger':
                            errors.has('expire') && value.refill > 0
                    }"
                ></date-picker>
            </div>
        </b-form-group>

        <b-form-group
            :label-for="`personalNote`"
            :label-cols="4"
            label="Personal Note:"
        >
            <b-form-textarea
                id="personalNote"
                :disabled="isLocked"
                :value="value.personal_note"
                @input="val => onChanged(val, 'personal_note')"
                placeholder="Message to the client that will be printed in the recommendation email from Vetsource. Including personal notes have been shown to significantly increase conversions into client orders. (optional)"
                name="personalNote"
                rows="3"
            ></b-form-textarea>
            <span
                v-bind:style="[
                    personalNoteCount >= 0
                        ? { color: 'grey' }
                        : { color: 'red' }
                ]"
                style="font-size: 10px; padding-left: 53%;"
                >{{ personalNoteCount }} character(s) remaining</span
            >
        </b-form-group>

        <b-form-group
            class="grooming"
            :label-for="`instruction`"
            :label-cols="4"
            label="Instructions:"
        >
            <editable
                ref="instruction"
                divId="instruction"
                name="instruction"
                :value="value.instruction"
                @input="val => onChanged(val, 'instruction')"
                className="unset"
                :disabled="isLocked"
                :required="true"
                :defaultHeight="81"
                placeholder="Usage instructions that will be printed on the Rx label (required)"
            ></editable>
            <span
                v-bind:style="[
                    instructionCount >= 0 ? { color: 'grey' } : { color: 'red' }
                ]"
                style="font-size: 10px; padding-left: 53%;"
                >{{ instructionCount }} character(s) remaining</span
            >
        </b-form-group>

        <b-form-group
            :label-for="`record_note`"
            :label-cols="4"
            label="Pharmacy Notes:"
        >
            <b-form-textarea
                id="record_note"
                name="record_note"
                :disabled="isLocked"
                :value="value.record_note"
                @input="val => onChanged(val, 'record_note')"
                placeholder="Clarifying information to the dispensing pharmacy (optional)"
                rows="3"
            ></b-form-textarea>
            <span
                v-bind:style="[
                    recordNoteCount >= 0 ? { color: 'grey' } : { color: 'red' }
                ]"
                style="font-size: 10px; padding-left: 53%;"
                >{{ recordNoteCount }} character(s) remaining</span
            >
        </b-form-group>
    </div>
</template>

<script>
import ScheduleHelper from "@/module/schedule/helper";
import RecordHelper from "@/mixins/record.js";
import vSelect from "vue-select";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import Editable from "@/components/elements/editable-textarea";
import { cloneDeep } from "lodash";

const DEFAULT_WEIGHT = { weight: null, uom: null };

export default {
    mixins: [ScheduleHelper, RecordHelper],
    components: {
        vSelect,
        Editable,
        vetDatetime
    },
    data() {
        return {
            weight: DEFAULT_WEIGHT
        };
    },
    props: {
        value: {
            type: Object,
            default: () => {
                return {
                    staff_id: null,
                    given_by_id: null,
                    item: null,
                    item_name: "",
                    quantity: 1,
                    uom: "",
                    expire: null,
                    instruction: "",
                    note: null,
                    record_note: "",
                    personal_note: "",
                    refill: 0,
                    given_by: "",
                    given_date: null,
                    next_due: "",
                    soap: ""
                };
            }
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        providers: {
            type: Array,
            default: () => []
        },
        givenByOptions: {
            type: Array,
            default: () => []
        },
        datePickerOptions: {
            type: Object,
            default: () => {}
        },
        medicalNotes: {
            type: Array,
            default: () => []
        },
        formError: {
            type: Array,
            default: () => []
        },
        instructionCount: {
            type: Number,
            default: RecordHelper.INSTRUCTION_COUNT
        },
        personalNoteCount: {
            type: Number,
            default: RecordHelper.PERSONAL_NOTE_COUNT
        },
        recordNoteCount: {
            type: Number,
            default: RecordHelper.RECORD_NOTE_COUNT
        }
    },
    mounted() {
        this.$validator.attach("instruction", "required");
    },
    methods: {
        onChanged(val, field) {
            let data = cloneDeep(this.value);
            data[field] = val;

            this.$emit("update", data);
        },
        async validate() {
            var instructionValidator = await this.$refs[
                "instruction"
            ].validate();
            var allValidator = await this.$validator.validateAll();
            return instructionValidator && allValidator;
        }
    }
};
</script>

<style></style>
